import LinearProgress from '@mui/material/LinearProgress';
import { alpha, styled } from '@mui/material/styles';
import { DialogContent } from 'components/ui/Dialog';
import { getPrismScrollbarStyles } from 'theme';

export const FirmDocumentUploadDialogBody = styled(DialogContent)({
  overflowY: 'hidden',
  '@media (max-height: 620px)': {
    overflowY: 'auto',
  },
});

export const FirmDocumentUploadDialogContent = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  maxHeight: 'calc(100vh - (var(--dialog-viewport-margin) * 2) - 210px)',
  minWidth: theme.spacing(76),
}));

interface FirmDocumentUploadDialogSelectGridStyleProps {
  columnCount: number;
}

const shouldForwardSelectGridProp = (prop: PropertyKey) => prop !== 'columnCount';

export const FirmDocumentUploadDialogSelectGrid = styled('div', {
  shouldForwardProp: shouldForwardSelectGridProp,
})<FirmDocumentUploadDialogSelectGridStyleProps>(({ columnCount, theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
  '@media (max-width: 760px)': {
    gridTemplateColumns: '1fr',
  },
}));

interface FirmDocumentUploadDialogDropzoneFrameStyleProps {
  isHidden: boolean;
}

const shouldForwardDropzoneFrameProp = (prop: PropertyKey) => prop !== 'isHidden';

export const FirmDocumentUploadDialogDropzoneFrame = styled('div', {
  shouldForwardProp: shouldForwardDropzoneFrameProp,
})<FirmDocumentUploadDialogDropzoneFrameStyleProps>(({ isHidden, theme }) => ({
  boxSizing: 'border-box',
  display: 'grid',
  filter: isHidden ? 'blur(1px)' : 'blur(0)',
  gridTemplateRows: isHidden ? '0fr' : '1fr',
  minWidth: 0,
  opacity: isHidden ? 0 : 1,
  transform: isHidden ? 'translateY(-16px) scale(0.96)' : 'translateY(0) scale(1)',
  transformOrigin: 'top center',
  transition: theme.transitions.create(['filter', 'grid-template-rows', 'opacity', 'transform'], {
    duration: theme.transitions.duration.complex + 150,
    easing: theme.transitions.easing.easeInOut,
  }),
}));

export const FirmDocumentUploadDialogDropzoneClip = styled('div', {
  shouldForwardProp: shouldForwardDropzoneFrameProp,
})<FirmDocumentUploadDialogDropzoneFrameStyleProps>(({ isHidden, theme }) => ({
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  padding: isHidden ? 0 : theme.spacing(0.5),
  transition: theme.transitions.create('padding', {
    duration: theme.transitions.duration.complex + 150,
    easing: theme.transitions.easing.easeInOut,
  }),
}));

export const FirmDocumentUploadDialogHelperText = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[500],
  fontSize: 'var(--dialog-description-font-size)',
  lineHeight: 'var(--dialog-description-line-height)',
}));

export const FirmDocumentUploadDialogStatusSlot = styled('div')({
  minHeight: 'var(--dialog-description-line-height)',
});

export const FirmDocumentUploadDialogTotalProgress = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.neutral[50],
  border: `var(--dialog-border-width) solid ${theme.palette.neutral[200]}`,
  borderRadius: theme.shape.radius.s,
  display: 'grid',
  gap: theme.spacing(0.75),
  minWidth: 0,
  padding: theme.spacing(1),
}));

export const FirmDocumentUploadDialogTotalProgressHeader = styled('span')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.neutral[600],
  display: 'flex',
  fontSize: 12,
  fontWeight: theme.typography.fontWeightMedium,
  justifyContent: 'space-between',
  lineHeight: '16px',
}));

export const FirmDocumentUploadDialogTotalProgressBar = styled(LinearProgress)(({ theme }) => ({
  borderRadius: theme.shape.radius.xs,
  height: 6,
  '&.MuiLinearProgress-root': {
    backgroundColor: alpha(theme.palette.brand[500], 0.12),
  },
  '& .MuiLinearProgress-bar': {
    backgroundColor: theme.palette.brand[500],
    borderRadius: theme.shape.radius.xs,
  },
}));

export const FirmDocumentUploadDialogSelectedFilesFrame = styled('div')(({ theme }) => ({
  minWidth: 0,
  position: 'relative',
  '--scroll-hidden-items-hint-font-size': '11px',
  '--scroll-hidden-items-hint-icon-size': '11px',
  '--scroll-hidden-items-hint-line-height': '14px',
  '--scroll-hidden-items-hint-offset': theme.spacing(0.5),
  '--scroll-hidden-items-hint-padding': '2px 6px',
}));

interface FirmDocumentUploadDialogSelectedFilesStyleProps {
  hasScrollGutter: boolean;
}

const shouldForwardSelectedFilesProp = (prop: PropertyKey) => prop !== 'hasScrollGutter';

export const FirmDocumentUploadDialogSelectedFiles = styled('div', {
  shouldForwardProp: shouldForwardSelectedFilesProp,
})<FirmDocumentUploadDialogSelectedFilesStyleProps>(({ hasScrollGutter, theme }) => ({
  display: 'grid',
  gap: theme.spacing(1),
  maxHeight: 'min(240px, 30vh)',
  minWidth: 0,
  overflowY: 'auto',
  boxSizing: 'border-box',
  paddingRight: hasScrollGutter ? theme.spacing(1.5) : 0,
  scrollPaddingBlock: theme.spacing(1),
  scrollbarGutter: hasScrollGutter ? 'stable' : 'auto',
  ...getPrismScrollbarStyles(theme, {
    thumbBorderColor: theme.palette.background.paper,
  }),
}));

interface FirmDocumentUploadDialogSelectedFileRowStyleProps {
  hasConflict?: boolean;
}

const shouldForwardSelectedFileRowProp = (prop: PropertyKey) => prop !== 'hasConflict';

export const FirmDocumentUploadDialogSelectedFileRow = styled('div', {
  shouldForwardProp: shouldForwardSelectedFileRowProp,
})<FirmDocumentUploadDialogSelectedFileRowStyleProps>(({ hasConflict = false, theme }) => ({
  alignItems: 'center',
  backgroundColor: hasConflict ? alpha(theme.palette.red[100], 0.45) : theme.palette.neutral[50],
  border: `var(--dialog-border-width) solid ${
    hasConflict ? alpha(theme.palette.red[400], 0.24) : theme.palette.neutral[200]
  }`,
  borderRadius: theme.shape.radius.s,
  boxSizing: 'border-box',
  display: 'grid',
  gap: theme.spacing(1),
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  minHeight: 48,
  minWidth: 0,
  overflow: 'hidden',
  padding: theme.spacing(1),
  position: 'relative',
  transition: theme.transitions.create(['background-color', 'border-color'], {
    duration: theme.transitions.duration.shortest,
  }),
}));

export const FirmDocumentUploadDialogSelectedFileIcon = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.shape.radius.xs,
  boxShadow: `inset 0 0 0 1px ${theme.palette.neutral[200]}`,
  display: 'inline-flex',
  flex: '0 0 auto',
  fontSize: 18,
  height: 28,
  justifyContent: 'center',
  width: 28,
}));

export const FirmDocumentUploadDialogSelectedFileContent = styled('span')({
  display: 'grid',
  minWidth: 0,
});

export const FirmDocumentUploadDialogSelectedFileName = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[700],
  fontSize: 'var(--dialog-description-font-size)',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 'var(--dialog-description-line-height)',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const FirmDocumentUploadDialogSelectedFileMeta = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[500],
  fontSize: 12,
  lineHeight: '16px',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const FirmDocumentUploadDialogRemoveFileButton = styled('button')(({ theme }) => ({
  alignItems: 'center',
  appearance: 'none',
  backgroundColor: 'transparent',
  border: 0,
  borderRadius: theme.shape.radius.xs,
  color: theme.palette.neutral[500],
  cursor: 'pointer',
  display: 'inline-flex',
  height: 24,
  justifyContent: 'center',
  padding: 0,
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 24,
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.neutral[200],
    color: theme.palette.neutral[900],
    outline: 0,
  },
  '&:disabled': {
    color: theme.palette.action.disabled,
    cursor: 'not-allowed',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 16,
  },
}));

export const FirmDocumentUploadDialogProgress = styled(LinearProgress)(({ theme }) => ({
  bottom: 0,
  height: 2,
  left: 0,
  position: 'absolute',
  right: 0,
  '&.MuiLinearProgress-root': {
    backgroundColor: alpha(theme.palette.brand[500], 0.12),
  },
  '& .MuiLinearProgress-bar': {
    backgroundColor: theme.palette.brand[500],
  },
}));

export const FirmDocumentUploadDialogRejectedFile = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.red[100], 0.45),
  border: `var(--dialog-border-width) solid ${alpha(theme.palette.red[400], 0.24)}`,
  borderRadius: theme.shape.radius.s,
  display: 'grid',
  gap: theme.spacing(1),
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  minHeight: 48,
  minWidth: 0,
  padding: theme.spacing(1),
}));

export const FirmDocumentUploadDialogRejectedFileIcon = styled(FirmDocumentUploadDialogSelectedFileIcon)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.red[400], 0.28)}`,
}));

export const FirmDocumentUploadDialogRejectedFileContent = styled('span')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(0.25),
  minWidth: 0,
}));

export const FirmDocumentUploadDialogRejectedFileName = styled(FirmDocumentUploadDialogSelectedFileName)(({ theme }) => ({
  color: theme.palette.neutral[700],
}));

export const FirmDocumentUploadDialogRejectedFileReason = styled('span')(({ theme }) => ({
  color: theme.palette.red[700],
  fontSize: 12,
  lineHeight: '16px',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));
